import { useState } from 'react';
import { CATEGORY_META, type SeedTray as Tray } from '@travel-planner/shared';
import {
  useAcceptStaged,
  useClearTray,
  useDismissStaged,
  useGenerateSeed,
  useSeedTray,
} from './useSeed';

export function SeedTray({ tripId, countryId }: { tripId: string; countryId: string }) {
  const tray = useSeedTray(tripId, countryId);
  const generate = useGenerateSeed(tripId, countryId);
  const clear = useClearTray(tripId, countryId);
  const [interests, setInterests] = useState('');

  const data = tray.data;
  const hasSuggestions = data && data.items.length > 0;

  return (
    <div
      className="rounded-3 p-3 mb-3"
      style={{ border: '1px solid var(--seed-border)', background: 'var(--seed-bg)' }}
    >
      <div className="d-flex gap-2 align-items-center flex-wrap">
        <strong className="d-flex align-items-center gap-1">
          <span aria-hidden style={{ color: 'var(--violet)' }}>✨</span> AI seed
        </strong>
        <input
          className="form-control form-control-sm"
          style={{ flex: 1, minWidth: 180 }}
          placeholder="interests (optional, e.g. hiking, wine)"
          value={interests}
          onChange={(e) => setInterests(e.target.value)}
        />
        <button
          className="btn btn-primary btn-sm"
          onClick={() => generate.mutate(interests.trim() || undefined)}
          disabled={generate.isPending}
        >
          {generate.isPending ? 'Generating…' : hasSuggestions ? 'Regenerate' : 'Suggest ideas'}
        </button>
        {hasSuggestions && (
          <button className="btn btn-ghost btn-sm text-danger" onClick={() => clear.mutate()}>
            Dismiss all
          </button>
        )}
      </div>

      {generate.isError && (
        <p className="text-danger small mt-2 mb-0">
          Couldn’t generate suggestions. Check the API key, or try again.
        </p>
      )}

      {hasSuggestions && data && <Suggestions tray={data} tripId={tripId} countryId={countryId} />}
    </div>
  );
}

function Suggestions({ tray, tripId, countryId }: { tray: Tray; tripId: string; countryId: string }) {
  const accept = useAcceptStaged(tripId, countryId);
  const dismiss = useDismissStaged(tripId, countryId);

  return (
    <div className="mt-2">
      <div className="mb-2" style={{ fontSize: '0.72rem', color: 'var(--violet)' }}>
        {tray.source === 'stub'
          ? 'Placeholder suggestions (no CLAUDE_API_KEY set) — accept to add, or dismiss.'
          : 'Review each suggestion: accept to add it, or dismiss.'}
      </div>
      {tray.items.map((it) => (
        <div
          key={it.id}
          className="d-flex align-items-center gap-2 py-1"
          style={{ borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}
        >
          <span className="flex-grow-1" style={{ minWidth: 0 }}>
            <Badge category={it.category} />
            <span className="ms-2">
              <strong>[{it.priority}]</strong> {it.title}
            </span>
            {it.lat != null && it.lng != null && (
              <span className="ms-2 text-body-tertiary" style={{ fontSize: '0.72rem' }}>📍</span>
            )}
            {it.note && <span className="ms-2 text-secondary">— {it.note}</span>}
          </span>
          <button
            className="btn btn-ghost btn-sm text-success"
            onClick={() => accept.mutate({ id: it.id })}
            disabled={accept.isPending || dismiss.isPending}
          >
            Accept
          </button>
          <button
            className="btn btn-ghost btn-sm text-body-tertiary"
            onClick={() => dismiss.mutate({ id: it.id })}
            disabled={accept.isPending || dismiss.isPending}
          >
            Dismiss
          </button>
        </div>
      ))}
    </div>
  );
}

function Badge({ category }: { category: keyof typeof CATEGORY_META }) {
  const m = CATEGORY_META[category];
  return (
    <span className="badge rounded-pill" style={{ color: '#fff', background: m.color, fontSize: '0.7rem' }}>
      {m.icon} {m.label}
    </span>
  );
}
